import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  Link,
  List,
  ListItemButton,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import AddIcon from '@mui/icons-material/Add';
import AccountTreeOutlinedIcon from '@mui/icons-material/AccountTreeOutlined';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  addDoc,
  collection,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { Link as RouterLink } from 'react-router-dom';
import { auth, db, functions } from '../../firebase';
import { DASHBOARD_TAB_PATHS } from '../../lib/dashboardRoutes';

const scaffoldAppRepo = httpsCallable(functions, 'scaffoldAppRepo');

/**
 * AI Studio — thin app_projects hub (create + link GitHub; no in-app browse).
 * @param {{ userData?: { companyId?: string, uid?: string } }} props
 */
export default function AiStudioDashboard({ userData }) {
  const companyId = userData?.companyId;
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState(null);
  const [newTitle, setNewTitle] = useState('');
  const [creating, setCreating] = useState(false);
  const [scaffolding, setScaffolding] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (!companyId) {
      setProjects([]);
      setLoading(false);
      return undefined;
    }
    setLoading(true);
    const q = query(
      collection(db, 'companies', companyId, 'app_projects'),
      orderBy('createdAt', 'desc'),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        setProjects(snap.docs.map((d) => ({ id: d.id, ...(d.data() || {}) })));
        setLoading(false);
      },
      (err) => {
        console.error('AiStudioDashboard projects:', err);
        setError(err?.message || 'Could not load projects.');
        setLoading(false);
      },
    );
    return () => unsub();
  }, [companyId]);

  const selected = useMemo(
    () => projects.find((p) => p.id === selectedId) || null,
    [projects, selectedId],
  );

  const handleCreate = async () => {
    const title = newTitle.trim();
    if (!companyId || !title) {
      setError('Enter a project title.');
      return;
    }
    const uid = auth.currentUser?.uid || userData?.uid || null;
    if (!uid) {
      setError('Sign in required.');
      return;
    }
    setCreating(true);
    setError('');
    setSuccess('');
    try {
      const ref = await addDoc(
        collection(db, 'companies', companyId, 'app_projects'),
        {
          title,
          createdAt: serverTimestamp(),
          createdByUid: uid,
          updatedAt: serverTimestamp(),
          updatedByUid: uid,
        },
      );
      setNewTitle('');
      setSelectedId(ref.id);
      setSuccess('Project created. Create a GitHub repo when ready.');
    } catch (err) {
      console.error('create app_project:', err);
      setError(err?.message || 'Could not create project.');
    } finally {
      setCreating(false);
    }
  };

  const handleScaffold = async () => {
    if (!selected?.id || !selected?.title) {
      setError('Select a saved project first.');
      return;
    }
    if (selected.githubRepoUrl) {
      setError('This project already has a GitHub repository.');
      return;
    }
    setScaffolding(true);
    setError('');
    setSuccess('');
    try {
      const result = await scaffoldAppRepo({
        projectId: selected.id,
        projectTitle: selected.title,
      });
      const data = result?.data || {};
      setSuccess(
        data.html_url
          ? `GitHub repository created: ${data.html_url}`
          : 'GitHub repository created.',
      );
    } catch (err) {
      console.error('scaffoldAppRepo:', err);
      setError(
        err?.message ||
          err?.details ||
          'Could not create GitHub repository. Connect a classic PAT under Workspace Settings → GitHub Integration.',
      );
    } finally {
      setScaffolding(false);
    }
  };

  if (!companyId) {
    return (
      <Alert severity="info" sx={{ maxWidth: 560 }}>
        Link your account to a company workspace to use AI Studio.
      </Alert>
    );
  }

  return (
    <Box>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
        <AutoAwesomeIcon color="primary" />
        <Typography variant="h4" component="h1" sx={{ fontWeight: 800 }}>
          AI Studio
        </Typography>
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2, maxWidth: 720 }}>
        Create app projects and link each to its own private GitHub repo using your
        company GitHub connection (Workspace Settings). In-app file browsing is a
        follow-up.
      </Typography>

      {error ? (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      ) : null}
      {success ? (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>
          {success}
        </Alert>
      ) : null}

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="stretch">
        <Paper variant="outlined" sx={{ p: 2, width: { md: 320 }, flexShrink: 0 }}>
          <Stack direction="row" spacing={1} sx={{ mb: 1.5 }}>
            <TextField
              size="small"
              label="New project title"
              value={newTitle}
              onChange={(e) => setNewTitle(e.target.value)}
              fullWidth
              disabled={creating}
            />
            <Button
              variant="contained"
              startIcon={creating ? <CircularProgress size={14} color="inherit" /> : <AddIcon />}
              onClick={() => void handleCreate()}
              disabled={creating || !newTitle.trim()}
              sx={{ whiteSpace: 'nowrap' }}
            >
              New
            </Button>
          </Stack>

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={22} />
            </Box>
          ) : projects.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Create your first project
            </Typography>
          ) : (
            <List dense disablePadding>
              {projects.map((p) => (
                <ListItemButton
                  key={p.id}
                  selected={p.id === selectedId}
                  onClick={() => setSelectedId(p.id)}
                >
                  <ListItemText
                    primary={p.title || '(untitled)'}
                    secondary={
                      p.githubRepoName
                        ? `${p.githubOwner}/${p.githubRepoName}`
                        : 'No repo yet'
                    }
                  />
                </ListItemButton>
              ))}
            </List>
          )}
        </Paper>

        <Paper variant="outlined" sx={{ p: 2, flex: 1 }}>
          {!selected ? (
            <Typography variant="body2" color="text.secondary">
              Select a project or create one to continue.
            </Typography>
          ) : (
            <Stack spacing={2}>
              <Typography variant="h6" fontWeight={700}>
                {selected.title || '(untitled)'}
              </Typography>
              <Divider />
              <Stack direction="row" spacing={1} alignItems="center">
                <AccountTreeOutlinedIcon color="primary" fontSize="small" />
                <Typography variant="subtitle2" fontWeight={700}>
                  Source control
                </Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary">
                Uses the same company GitHub connection as Game Studio. Configure it in{' '}
                <Link
                  component={RouterLink}
                  to={DASHBOARD_TAB_PATHS.Workspace}
                  underline="hover"
                >
                  Workspace Settings → GitHub Integration
                </Link>
                .
              </Typography>

              {selected.githubRepoUrl ? (
                <Alert
                  severity="success"
                  action={
                    <Button
                      color="inherit"
                      size="small"
                      endIcon={<OpenInNewIcon fontSize="small" />}
                      href={selected.githubRepoUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      Open on GitHub
                    </Button>
                  }
                >
                  Linked:{' '}
                  <Link
                    href={selected.githubRepoUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {selected.githubOwner}/{selected.githubRepoName}
                  </Link>
                </Alert>
              ) : (
                <Button
                  variant="outlined"
                  startIcon={
                    scaffolding ? (
                      <CircularProgress size={16} color="inherit" />
                    ) : (
                      <AccountTreeOutlinedIcon />
                    )
                  }
                  onClick={() => void handleScaffold()}
                  disabled={scaffolding}
                >
                  Create GitHub repo
                </Button>
              )}
            </Stack>
          )}
        </Paper>
      </Stack>
    </Box>
  );
}
